import { useVisitorContext } from "@/contexts/VisitorTrackingContext";
import type { PersonalisationDecisions } from "@/hooks/useVisitorTracking";

type Pages = NonNullable<PersonalisationDecisions["pages"]>;

/**
 * Returns personalisation overrides for a specific page, with null fallbacks.
 * Usage: const p = usePagePersonalisation("homepage");
 *        const headline = p?.hero_headline || defaultHeadline;
 */
export function usePagePersonalisation<K extends keyof Pages>(
  pageKey: K
): Pages[K] | null {
  const { personalisation, dynamicMode } = useVisitorContext();

  // Auto-bypass personalisation in Lovable preview and when ?static=true
  if (typeof window !== "undefined") {
    const loc = window.location;
    const isLovablePreview = loc.hostname.includes("lovableproject.com") || 
      (loc.hostname.includes("lovable.app") && loc.hostname.includes("preview"));
    const isStatic = new URLSearchParams(loc.search).get("static") === "true";
    if (isLovablePreview || isStatic) {
      return null;
    }
  }
  
  // Dynamic Mode is the master switch. If the visitor has not opted in
  // (or has opted out / been reverted), never serve personalised overrides
  // — even if a stale cache still exists in memory.
  if (!dynamicMode) return null;

  if (!personalisation?.pages) return null;
  return (personalisation.pages[pageKey] as Pages[K]) ?? null;
}

/**
 * Returns the full personalisation decisions (for handoff prompts, etc.)
 */
export function usePersonalisation(): PersonalisationDecisions | null {
  const { personalisation } = useVisitorContext();
  return personalisation;
}
